import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Factory, LogOut, ArrowLeft, ShieldCheck, KeyRound, Upload, Send, CheckCircle2, Image as ImageIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sp = axios.create({ baseURL: API });
sp.interceptors.request.use((c) => { const t = localStorage.getItem("sp_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });
const errMsg = (e) => e?.response?.data?.detail || "Fehler";

const SC_STATUS = {
  open: ["bg-gray-100 text-gray-700", "Offen"], in_8d: ["bg-amber-100 text-amber-800", "8D läuft"],
  effectiveness_pending: ["bg-blue-100 text-blue-700", "Wirksamkeit geprüft"], closed: ["bg-green-100 text-green-700", "Geschlossen"],
};
const D_LABELS = { d1: "Team", d2: "Problembeschreibung", d3: "Sofortmaßnahmen", d4: "Ursachenanalyse", d5: "Korrekturmaßnahmen", d6: "Umsetzung", d7: "Vorbeugung", d8: "Abschluss" };

function Frame({ children }) {
  return <div className="min-h-screen bg-[#1B2A4A] flex items-center justify-center p-4">
    <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-sm space-y-5" data-testid="sp-auth-card">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded bg-[#F5A623] flex items-center justify-center"><Factory className="h-5 w-5 text-[#1B2A4A]" strokeWidth={2.5} /></div>
        <div><p className="font-bold leading-none">Encasa Lieferantenportal</p><p className="text-xs text-gray-400 mt-0.5">Reklamationen & Schriftverkehr</p></div>
      </div>
      {children}
    </div>
  </div>;
}

export default function SupplierPortal() {
  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get("invite");
  const [phase, setPhase] = useState("checking"); // checking|invite|login|mfa|dashboard
  const [user, setUser] = useState(null);
  const [invite, setInvite] = useState(null);
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [mfa, setMfa] = useState(null); // {mfa_token, setup, secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [complaints, setComplaints] = useState([]);
  const [view, setView] = useState("list");
  const [detail, setDetail] = useState(null);
  const [corr, setCorr] = useState([]); const [files, setFiles] = useState([]);
  const [resp, setResp] = useState({ body: "", d3: "", d4: "", d5: "" });
  const [busy, setBusy] = useState(false);

  const loadComplaints = useCallback(() => { sp.get("/supplier-portal/complaints").then((r) => setComplaints(r.data || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (inviteToken) {
      axios.post(`${API}/supplier-portal/invite/validate`, { token: inviteToken })
        .then((r) => { setInvite(r.data); setPhase("invite"); })
        .catch((e) => { toast.error(errMsg(e)); setPhase("login"); });
      return;
    }
    const t = localStorage.getItem("sp_token");
    if (!t) { setPhase("login"); return; }
    sp.get("/supplier-portal/me").then((r) => { setUser(r.data); setPhase("dashboard"); }).catch(() => { localStorage.removeItem("sp_token"); setPhase("login"); });
  }, [inviteToken]);

  useEffect(() => { if (phase === "dashboard") loadComplaints(); }, [phase, loadComplaints]);

  const doSetPassword = async () => {
    if (pw.length < 8) { toast.error("Mindestens 8 Zeichen"); return; }
    if (pw !== pw2) { toast.error("Passwörter stimmen nicht überein"); return; }
    setBusy(true);
    try { await axios.post(`${API}/supplier-portal/set-password`, { token: inviteToken, password: pw });
      toast.success("Passwort gesetzt. Bitte anmelden.");
      window.history.replaceState({}, "", "/supplier-portal");
      setLoginForm({ email: invite.email, password: "" }); setPhase("login");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/supplier-portal/login`, loginForm);
      if (r.data.mfa_setup_required) { setMfa({ mfa_token: r.data.mfa_token, setup: true, secret: r.data.secret, otpauth_uri: r.data.otpauth_uri }); setPhase("mfa"); }
      else if (r.data.mfa_required) { setMfa({ mfa_token: r.data.mfa_token, setup: false }); setPhase("mfa"); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doMfa = async () => {
    setBusy(true);
    try {
      const url = mfa.setup ? "/supplier-portal/mfa/setup-verify" : "/supplier-portal/mfa/verify";
      const r = await axios.post(`${API}${url}`, { mfa_token: mfa.mfa_token, code });
      localStorage.setItem("sp_token", r.data.access_token);
      setUser(r.data.user); setCode(""); setMfa(null); setPhase("dashboard");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const logout = () => { localStorage.removeItem("sp_token"); setUser(null); setPhase("login"); };

  const openDetail = async (c) => {
    setDetail(c); setView("detail"); setResp({ body: "", d3: "", d4: "", d5: "" });
    sp.get(`/supplier-portal/complaints/${c.id}/correspondence`).then((r) => setCorr(r.data || [])).catch(() => {});
    sp.get(`/supplier-portal/complaints/${c.id}/files`).then((r) => setFiles(r.data || [])).catch(() => {});
  };
  const refreshDetail = async (id) => {
    const r = await sp.get(`/supplier-portal/complaints/${id}`); setDetail(r.data);
    sp.get(`/supplier-portal/complaints/${id}/correspondence`).then((x) => setCorr(x.data || [])).catch(() => {});
  };
  const acknowledge = async () => { try { await sp.post(`/supplier-portal/complaints/${detail.id}/acknowledge`); toast.success("Bestätigt"); refreshDetail(detail.id); loadComplaints(); } catch (e) { toast.error(errMsg(e)); } };
  const sendResponse = async () => {
    setBusy(true);
    try { await sp.post(`/supplier-portal/complaints/${detail.id}/respond`, resp); toast.success("Rückmeldung gesendet"); setResp({ body: "", d3: "", d4: "", d5: "" }); refreshDetail(detail.id); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await sp.post(`/supplier-portal/complaints/${detail.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Datei hochgeladen"); sp.get(`/supplier-portal/complaints/${detail.id}/files`).then((r) => setFiles(r.data || [])); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (phase === "checking") return <div className="min-h-screen bg-[#1B2A4A] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-white/30 border-t-[#F5A623] animate-spin" /></div>;

  if (phase === "invite") return (
    <Frame>
      <div className="space-y-3" data-testid="sp-invite-form">
        <p className="text-sm text-gray-600">Willkommen, {invite.contact_name || invite.email}. Legen Sie ein Passwort für <b>{invite.supplier_name}</b> fest.</p>
        <div className="space-y-1"><Label className="text-xs text-gray-500">Passwort (min. 8 Zeichen)</Label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="sp-pw-input" /></div>
        <div className="space-y-1"><Label className="text-xs text-gray-500">Passwort wiederholen</Label>
          <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="sp-pw2-input" /></div>
        <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={doSetPassword} disabled={busy} data-testid="sp-setpw-btn">Passwort festlegen</Button>
      </div>
    </Frame>
  );

  if (phase === "login") return (
    <Frame>
      <div className="space-y-3" data-testid="sp-login-form">
        <div className="space-y-1"><Label className="text-xs text-gray-500">E-Mail</Label>
          <Input type="email" value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} data-testid="sp-email-input" /></div>
        <div className="space-y-1"><Label className="text-xs text-gray-500">Passwort</Label>
          <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && doLogin()} data-testid="sp-password-input" /></div>
        <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={doLogin} disabled={busy} data-testid="sp-login-btn">Anmelden</Button>
      </div>
    </Frame>
  );

  if (phase === "mfa") return (
    <Frame>
      <div className="space-y-3" data-testid="sp-mfa-form">
        <div className="flex items-center gap-2 text-sm font-medium text-[#1B2A4A]"><ShieldCheck className="h-4 w-4" />Zwei-Faktor-Authentifizierung</div>
        {mfa.setup && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 space-y-1.5">
            <p className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" />Fügen Sie diesen Schlüssel in Ihre Authenticator-App ein:</p>
            <code className="block bg-white rounded px-2 py-1 font-mono text-sm tracking-wider break-all" data-testid="sp-mfa-secret">{mfa.secret}</code>
          </div>
        )}
        <div className="space-y-1"><Label className="text-xs text-gray-500">6-stelliger Code</Label>
          <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doMfa()} data-testid="sp-mfa-code" /></div>
        <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={doMfa} disabled={busy || code.length < 6} data-testid="sp-mfa-verify-btn">Bestätigen</Button>
      </div>
    </Frame>
  );

  const token = localStorage.getItem("sp_token");
  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="sp-dashboard">
      <header className="bg-[#1B2A4A] text-white px-4 sm:px-8 py-4 flex items-center gap-3">
        <div className="h-8 w-8 rounded bg-[#F5A623] flex items-center justify-center"><Factory className="h-4 w-4 text-[#1B2A4A]" strokeWidth={2.5} /></div>
        <div className="flex-1 min-w-0"><p className="font-bold leading-none truncate">Encasa Lieferantenportal</p>
          <p className="text-xs text-white/50 truncate">{user?.supplier_name} · {user?.contact_name || user?.email}</p></div>
        <button onClick={logout} className="p-2 rounded hover:bg-white/10" data-testid="sp-logout-btn"><LogOut className="h-4 w-4" /></button>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-8 space-y-4">
        {view === "list" && (<>
          <h1 className="text-2xl font-bold tracking-tight">Meine Reklamationen</h1>
          {complaints.length === 0 && <p className="text-gray-400 text-sm">Keine Reklamationen.</p>}
          <div className="space-y-3">
            {complaints.map((c) => {
              const [cls, lbl] = SC_STATUS[c.status] || ["bg-slate-100 text-slate-600", c.status];
              return (
                <button key={c.id} onClick={() => openDetail(c)} data-testid="sp-complaint-card"
                  className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-[#1B2A4A]">{c.complaint_number}</span>
                    <div className="flex items-center gap-1.5">
                      {c.acknowledged_at && <Badge className="bg-emerald-50 text-emerald-700">Bestätigt</Badge>}
                      <Badge className={cls}>{lbl}</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{c.defect_category || "–"} · {c.severity}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{new Date(c.created_at).toLocaleDateString("de-DE")} {c.article_name && `· ${c.article_name}`}</p>
                </button>
              );
            })}
          </div>
        </>)}

        {view === "detail" && detail && (
          <div className="space-y-4" data-testid="sp-detail">
            <button onClick={() => { setView("list"); setDetail(null); }} className="text-sm text-gray-500 flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Zurück</button>
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-mono font-bold text-[#1B2A4A]">{detail.complaint_number}</h1>
                <Badge className={(SC_STATUS[detail.status] || ["bg-slate-100 text-slate-600"])[0]}>{(SC_STATUS[detail.status] || ["", detail.status])[1]}</Badge>
              </div>
              <p className="text-sm text-gray-600">{detail.defect_category} · {detail.severity} · {detail.article_name || "–"} · Charge {detail.batch || "–"}</p>
              {detail.description && <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.description}</p>}
              {!detail.acknowledged_at
                ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={acknowledge} data-testid="sp-ack-btn"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Reklamation bestätigen</Button>
                : <p className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Bestätigt am {new Date(detail.acknowledged_at).toLocaleDateString("de-DE")}</p>}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold mb-3">8D-Report (Status)</h3>
              <div className="space-y-1.5">
                {Object.entries(D_LABELS).map(([k, l]) => (
                  <div key={k} className="flex gap-2 text-sm">
                    <span className={`w-5 h-5 shrink-0 rounded-full text-[10px] flex items-center justify-center font-bold ${detail.eight_d?.[k]?.done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>{k.toUpperCase().replace("D", "")}</span>
                    <div><span className="text-gray-500">{l}: </span><span className="text-gray-700">{detail.eight_d?.[k]?.content || "–"}</span></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-3" data-testid="sp-respond">
              <h3 className="text-sm font-semibold">Rückmeldung / 8D-Beitrag</h3>
              <Textarea rows={2} placeholder="Nachricht an das Qualitätsteam" value={resp.body} onChange={(e) => setResp((r) => ({ ...r, body: e.target.value }))} data-testid="sp-resp-body" />
              <div className="grid grid-cols-1 gap-2">
                <Input placeholder="D3 – Sofortmaßnahmen" value={resp.d3} onChange={(e) => setResp((r) => ({ ...r, d3: e.target.value }))} data-testid="sp-resp-d3" />
                <Input placeholder="D4 – Ursachenanalyse" value={resp.d4} onChange={(e) => setResp((r) => ({ ...r, d4: e.target.value }))} data-testid="sp-resp-d4" />
                <Input placeholder="D5 – Korrekturmaßnahmen" value={resp.d5} onChange={(e) => setResp((r) => ({ ...r, d5: e.target.value }))} data-testid="sp-resp-d5" />
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63]" onClick={sendResponse} disabled={busy} data-testid="sp-resp-send"><Send className="h-3.5 w-3.5 mr-1" />Senden</Button>
                <label className="text-sm text-gray-500 flex items-center gap-1 cursor-pointer">
                  <Upload className="h-3.5 w-3.5" />Datei
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} data-testid="sp-file-input" />
                </label>
              </div>
              {files.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {files.map((f) => (f.content_type?.startsWith("image/")
                    ? <a key={f.id} href={`${API}/files/download/${f.id}?auth=${token}`} target="_blank" rel="noreferrer"><img src={`${API}/files/download/${f.id}?auth=${token}`} alt={f.original_filename} className="rounded border object-cover h-16 w-full" /></a>
                    : <a key={f.id} href={`${API}/files/download/${f.id}?auth=${token}`} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 underline flex items-center justify-center h-16 border rounded px-1"><ImageIcon className="h-3 w-3 mr-1" />{f.original_filename}</a>))}
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold mb-3">Schriftverkehr</h3>
              {corr.length === 0 && <p className="text-xs text-gray-400">Noch kein Schriftverkehr.</p>}
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {corr.map((r) => (
                  <div key={r.id} className="border border-gray-100 rounded-lg p-2.5 text-sm" data-testid="sp-corr-item">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className={r.direction === "in" ? "text-emerald-600" : "text-blue-600"}>{r.direction === "in" ? "→ eingehend" : "← ausgehend"}</span>
                      <span>· {r.channel}</span><span>· {new Date(r.created_at).toLocaleString("de-DE")}</span>
                    </div>
                    {r.subject && <p className="font-medium text-gray-800 mt-1">{r.subject}</p>}
                    {r.body && <p className="text-gray-600 whitespace-pre-wrap mt-0.5">{r.body}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
